import React, { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import _ from 'lodash';

import { SwButtonSection, getFetcher, PRODUCT_TITLE } from '../../../App';
import { Input, Select, PrimaryButton, Sub1Button, Checkbox, DefaultButton } from '@components/atoms';
import { SettingsToggle } from '@components/molecules';

import { SettingWrapper, SettingBox, SettingList, InputList, SelectBox, StyledSlice } from './style';
import SWSettingCommonMethods from '../../../utils/settingCommon';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { dialogContentsState, noneChangePeriodState } from '@components/templates/Dialog';
import { useCatchLeave } from '../../../utils/hooks';
import moment from 'moment';

import { debounce, REGEX_NUMBER } from '@variables/utils';
import SelectSearch from '@components/atoms/Inputs/SelectSearch';
import { submitDebounce } from '../../../utils/lib';

interface SettingType {
  id: number;
  companyId: number;
  defaultLanguage: string;
  languages: language[];
  fileFormats: fileFormat[];
  isStorageWithoutTimeLimit: boolean;
  storagePeriod: number;
  dateFormat: string;
  encodings: encodings[];
  sliceMax: string | null;
  sliceMin: string | null;
  timeout: string;
  timeZone: string;
  maximumTimeout: string;
  diskCleanupRecoveryPercent: number;
  diskFreeSpaceThresholdPercent: number;
}

type encodings = {
  item: string;
  active: boolean;
};

type language = {
  active: boolean;
  language: string;
};

type fileFormat = {
  active: boolean;
  format: string;
};

type selectList = {
  label: string;
  value: string;
  desc?: string;
  selected?: boolean;
};

type PostParams = {
  activeLanguages: string[];
  dateFormat: string;
  encoding: string;
  fileFormat: string;
  isStorageWithoutTimeLimit: boolean;
  language: string;
  storagePeriod: number;
  sliceMin: number | null;
  sliceMax: number | null;
  timeout: number;
  maximumTimeout: number;
  diskCleanupRecoveryPercent: number;
  diskFreeSpaceThresholdPercent: number;
  timeZone: string;
};

type MenuListType = { enable: boolean; name: string };
const LanguageSettingPage = () => {
  const [menuList, setMenuList] = useState<MenuListType[]>([]);
  const [settingData, setSettingData] = useState<SettingType>();
  const [originSettingData, setOriginSettingData] = useState<SettingType>();
  const { errorHandler } = SWSettingCommonMethods();
  const [defaultLanguageList, setDefaultLanguageList] = useState<selectList[]>([]);
  const [encofingList, setEncodingList] = useState<selectList[]>([]);
  const [limitDayList, setLimitDayList] = useState<selectList[]>([]);
  const [timeZoneList, setTimeZoneList] = useState<selectList[]>([]);
  const [activeSubmit, setActiveSubmit] = useState(false);
  const setDialogContents = useSetRecoilState(dialogContentsState);
  const setNoneChangePeriod = useSetRecoilState(noneChangePeriodState);

  // 값 변경이 있는 경우 새로고침, 탭닫기 막기
  useCatchLeave(!activeSubmit);

  const DateFormatList = [
    { name: 'DateFormat', value: 'YYYY-MM-DD' },
    { name: 'DateFormat', value: 'DD-MM-YYYY' },
    { name: 'DateFormat', value: 'MM-DD-YYYY' },
    { name: 'DateFormat', value: 'DD-MMM-YYYY' },
  ];

  const AutoDelete = [
    { label: '50%', value: '50' },
    { label: '40%', value: '40' },
    { label: '30%', value: '30' },
    { label: '20%', value: '20' },
  ];

  const DeleteRate = [
    { label: '40%', value: '40' },
    { label: '30%', value: '30' },
    { label: '20%', value: '20' },
    { label: '10%', value: '10' },
  ];

  /**
   * setting값 변경처리
   * @param e
   * @param type
   * @param idxD
   */
  const changeSetting = (e: any, type: string, idx?: number) => {
    const _data = JSON.parse(JSON.stringify(settingData));

    let _value = e.target?.value;
    let _valueNum;
    if (!_value) {
      _value = e;
    }
    if (_value) {
      _valueNum = typeof _value === 'string' ? _value.replace(REGEX_NUMBER, '') : null;
    }

    const _idx = String(idx);

    // 체크된 언어 추가
    let _availableLang: selectList[] = [];
    switch (type) {
      case 'MULTILINGUAL_LANG':
        const activeLang = _data.languages.filter((item: { language: string; active: boolean }) => item.active);
        if (activeLang.length === 1 && activeLang[0].language === e.target.name) {
          return;
        }
        _data.languages[_idx].active = e.target.checked;

        _data.languages.map((lang: { language: string; active: boolean }) => {
          if (lang.active) {
            _availableLang.push({
              label: lang.language,
              value: lang.language,
              selected: _data.defaultLanguage === lang.language,
            });
          }
        });

        // 기본 언어 리스트가 1개 일 경우에는 해당 언어가 디폴트 언어로 세팅
        const langResult1 = _availableLang.map((lang) => lang.label);
        const langResult2 = langResult1.filter((item) => item === _data.defaultLanguage);
        if (_availableLang.length === 1 || langResult2.length === 0) {
          _data.defaultLanguage = _availableLang[0].label;
        } else {
          if (langResult2) {
            _data.defaultLanguage = langResult2[0];
          } else {
            _data.defaultLanguage = langResult1[0];
          }
        }

        // 체크된 언어에 따라 기본 언어 리스트 변경
        setDefaultLanguageList(_availableLang);

        break;
      case 'DEFAULT_LANG':
        _value = e.value;
        _data.defaultLanguage = _value;

        defaultLanguageList.map((lang) => {
          _availableLang.push({
            label: lang.label,
            value: lang.value,
            selected: _data.defaultLanguage === lang.label,
          });
        });

        setDefaultLanguageList(_availableLang);
        break;
      case 'FILE_FORMAT':
        // Todo 나중에 수정 해야함
        const _backIdx = idx === 0 ? 1 : 0;
        _data.fileFormats[_idx].active = e.target.checked;
        _data.fileFormats[_backIdx].active = false;
        break;
      case 'STORE_LIMIT':
        _data.isStorageWithoutTimeLimit = _value === 'true';
        break;
      case 'STORE_LIMIT_DAY':
        _value = e.value;
        _data.storagePeriod = Number(_value);
        break;
      case 'STORE_AUTO_DELETE':
        _value = e.value;
        _data.diskFreeSpaceThresholdPercent = parseInt(_value);
        break;
      case 'STORE_DELETE_RATE':
        _value = e.value;
        _data.diskCleanupRecoveryPercent = parseInt(_value);
        break;
      case 'TIME_ZONE':
        _data.timeZone = _value;
        break;
      case 'DATE_FORMAT':
        _data.dateFormat = _value;
        break;
      case 'ENCODING_FORMAT':
        _data.encodings.forEach((data: encodings) => {
          data.active = e.label === data.item;
        });
        break;
      case 'IDENTICAL_SERIES_CHECK_TIMEOUT':
        _data.timeout = !_valueNum ? null : Number(_valueNum) === 0 ? '0' : Number(_valueNum);
        break;
      case 'IDENTICAL_SERIES_CHECK_MAXIMUM_TIMEOUT':
        _data.maximumTimeout = !_valueNum ? null : Number(_valueNum) === 0 ? '0' : Number(_valueNum);
        break;
      case 'IDENTICAL_SERIES_CHECK_SLICE_MIN':
        _data.sliceMin = !_valueNum ? null : Number(_valueNum) === 0 ? '0' : Number(_valueNum);
        break;
      case 'IDENTICAL_SERIES_CHECK_SLICE_MAX':
        _data.sliceMax = !_valueNum ? null : Number(_valueNum) === 0 ? '0' : Number(_valueNum);
        break;
    }

    setSettingData(_data);
  };

  /**
   * timeout
   * 최대값, 최소값 적용 및 빈 값일 경우 기본값(60) 적용
   * @param e
   * @param type
   * @param idx
   */
  const timeoutBlur = (e: any, type: string) => {
    const _data = JSON.parse(JSON.stringify(settingData));
    let _value = e.target?.value;
    let _valueNum = _value.replace(REGEX_NUMBER, '');

    // 앱마다 다른 기본값이 될 수 있음
    // stroke - 5, 그외 기본값  60
    let defaultValue;
    switch (PRODUCT_TITLE) {
      case 'Heuron StroCare Suite':
        defaultValue = '5';
        break;
      default:
        defaultValue = '60';
        break;
    }

    const test = !_valueNum
      ? (_value = defaultValue)
      : Number(_valueNum) === 0 || Number(_valueNum) < 5
      ? (_value = '5')
      : Number(_valueNum) > 3600
      ? (_value = '3600')
      : _value;

    switch (type) {
      case 'IDENTICAL_SERIES_CHECK_TIMEOUT':
        _data.timeout = Number(test);
        // if (_data.maximumTimeout < Number(test)) _data.maximumTimeout = Number(test);
        break;
      case 'IDENTICAL_SERIES_CHECK_MAXIMUM_TIMEOUT':
        _data.maximumTimeout = Number(test);
        // if (_data.timeout > Number(test)) _data.maximumTimeout = _data.timeout;
        break;
    }
    setSettingData(_data);
  };

  /**
   * Identical Series check - onBlur
   * @param blurVal
   * @param type
   * */
  const onBlurSliceTimeMinMax = (type: string, blurVal: number | string) => {
    const _blurVal = blurVal.toString();
    const numBlurVal = parseInt(_blurVal);

    const timeoutMin = 5;
    const timeoutMax = 3600;
    const timeDefault = 60;

    const sliceMin = 0;
    const sliceMax = 10000;

    if (!settingData) {
      return;
    }
    //데이타 복사
    const _settingData = { ...settingData };

    if (type === 'timeout') {
      // 인풋값이 없을 때
      if (_blurVal === '') {
        // console.log('비어있네');
        // 타임아웃 기본값(60) 입력
        setSettingData({ ..._settingData, timeout: timeDefault.toString() });
        return;
      }

      if (numBlurVal < timeoutMin) {
        //입력값이 최소값보다 작을 때, 최소값(5)입력
        setSettingData({ ..._settingData, timeout: timeoutMin.toString() });
      } else if (numBlurVal > timeoutMax) {
        //입력값이 최대값보다 클 때, 최대값(3600)입력
        setSettingData({ ..._settingData, timeout: timeoutMax.toString() });
      }
    } else if (type === 'min') {
      if (_blurVal === '') {
        // console.log('min 비어있네')
        setSettingData({ ..._settingData, sliceMin: null, sliceMax: null });
        return;
      }

      if (numBlurVal > parseInt(_settingData.sliceMax ? _settingData.sliceMax : '')) {
        //입력값이 sliceMax보다 클 때, 최소값 => 최대값으로 강제입력
        setSettingData({
          ..._settingData,
          sliceMin: _settingData.sliceMax ? _settingData.sliceMax : null,
        });
      }
      if (numBlurVal < sliceMin) {
        //입력값이 0 미만시, 최소값 => 0으로 강제입력
        setSettingData({ ..._settingData, sliceMin: sliceMin.toString() });
      }
    } else if (type === 'max') {
      if (_blurVal === '') {
        // console.log('max 비어있네')
        setSettingData({ ..._settingData, sliceMin: null, sliceMax: null });
        return;
      }

      if (numBlurVal < parseInt(_settingData.sliceMin ? _settingData.sliceMin : '')) {
        //입력값이 sliceMin보다 작을 때, 최대값 => 최소값으로 강제입력
        setSettingData({
          ..._settingData,
          sliceMax: _settingData.sliceMin ? _settingData.sliceMin : null,
        });
      }
      if (numBlurVal > sliceMax) {
        //입력값이 10000 초과시, 최대값 => 10000으로 강제입력
        setSettingData({ ..._settingData, sliceMax: sliceMax ? sliceMax.toString() : null });
      }
    }
  };

  /**
   *
   */
  const confirmStorageSetting = (event: any, type: string) => {
    setDialogContents({
      type: 'plain',
      body: (
        <>
          <span style={{ fontWeight: '500', color: '#ff4c4c' }}>
            If you change the storage setting,
            <br />
            some data may be deleted.
          </span>
          <div style={{ marginTop: 15 }}>
            <Checkbox onChange={(e) => setNoneChangePeriod(!e.target.checked)}>You have verified this.</Checkbox>
          </div>
        </>
      ),
      footer: (
        <>
          <DefaultButton
            onClick={() => {
              setDialogContents({ type: 'hidden' });
            }}
          >
            Cancel
          </DefaultButton>
          <PrimaryButton
            className={'btn-active-target'}
            onClick={async () => {
              const _target = document.getElementsByClassName('btn-active');

              if (_target.length === 0) {
                changeSetting(event, type);
                setNoneChangePeriod(true);
                setDialogContents({ type: 'hidden' });
              }
            }}
          >
            Confirm
          </PrimaryButton>
        </>
      ),
    });
  };

  /**
   * Server Storage period 변경 때 마다 확인 컨펌 노출
   */
  const confirmStoragePeriod = (event: any, type: string) => {
    setDialogContents({
      type: 'plain',
      body: (
        <>
          <span style={{ fontWeight: '500', color: '#ff4c4c' }}>
            If you change the storage setting,
            <br />
            some data may be deleted.
          </span>
          <div style={{ marginTop: 15 }}>
            <Checkbox onChange={(e) => setNoneChangePeriod(!e.target.checked)}>You have verified this.</Checkbox>
          </div>
        </>
      ),
      footer: (
        <>
          <DefaultButton
            onClick={() => {
              setDialogContents({ type: 'hidden' });
            }}
          >
            Cancel
          </DefaultButton>
          <PrimaryButton
            className={'btn-active-target'}
            onClick={async () => {
              const _target = document.getElementsByClassName('btn-active');

              if (_target.length === 0) {
                changeSetting(event, type);
                setNoneChangePeriod(true);
                setDialogContents({ type: 'hidden' });
              }
            }}
          >
            Confirm
          </PrimaryButton>
        </>
      ),
    });
    return;
  };

  /**
   * 최초 기본 언어 select option, automatically select option 생성
   * @param data
   */
  const makeSelectList = (data: any) => {
    let _tmpLangList: any = [];
    let _tmpEncodingList: any = [];
    let _tmpDayList: any = [
      { label: 'Immediately', value: '0' },
      { label: '1 day', value: '1' },
      { label: '7 days', value: '7' },
      { label: '30 days', value: '30' },
      { label: '180 days', value: '180' },
      { label: '365 days', value: '365' },
      // { label: '730', value: '730' },
      // { label: '1825', value: '1825' },
    ];

    //기본 언어 select option
    data.languages.forEach((lang: any) => {
      const _tmp = {
        label: lang.language,
        value: lang.language,
        selected: data.defaultLanguage === lang.language,
      };
      if (lang.active) {
        _tmpLangList.push(_tmp);
      }
    });
    setDefaultLanguageList(_tmpLangList);
    //기본 인코딩 select option
    data.encodings.forEach((encoding: any) => {
      const _tmp = {
        label: encoding.item,
        value: encoding.item,
        selected: encoding.active,
      };
      _tmpEncodingList.push(_tmp);
    });

    setEncodingList(_tmpEncodingList);

    // automatically select option
    _tmpDayList.forEach((dayOption: any) => {
      dayOption.selected = dayOption.value === String(data.storagePeriod);
    });

    setLimitDayList(_tmpDayList);
  };

  /**
   * setting 데이터 가져오기
   */
  const getSettingData = async () => {
    const url = 'api/sw/setting';
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          makeSelectList(r.result);
          setSettingData(r.result);
          setOriginSettingData(r.result);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * setting 데이터 가져오기
   */
  const getTimeZone = async () => {
    const url = 'api/sw/timezone';
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          const _tmpList: selectList[] = [];
          for (let key in r.result) {
            const _tmp = {
              label:
                r.result[key].timeZone +
                ` (${r.result[key].offset === 'Z' ? r.result[key].offset : 'UTC' + r.result[key].offset})`,
              value: r.result[key].timeZone,
            };
            _tmpList.push(_tmp);
          }
          setTimeZoneList(_tmpList);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * submit 전에 변경 확인 컨펌 노출
   * period 변경시 컨펌, 아닐 경우 변경
   */
  const confirmSubmit = () => {
    const _isSame = _.isEqual(settingData, originSettingData);
    const _changePeriod =
      settingData?.isStorageWithoutTimeLimit !== originSettingData?.isStorageWithoutTimeLimit ||
      settingData?.storagePeriod !== originSettingData?.storagePeriod;
    settingData?.diskCleanupRecoveryPercent !== originSettingData?.diskCleanupRecoveryPercent ||
      settingData?.diskFreeSpaceThresholdPercent !== originSettingData?.diskFreeSpaceThresholdPercent ||
      settingData?.defaultLanguage !== originSettingData?.defaultLanguage;

    // 아예 변경이 없거나 , period 변경이 없을 경우
    if (!_changePeriod || _isSame) {
      postSettingData();
    } else {
      setDialogContents({
        type: 'warning',
        body: (
          <>
            <span
              style={{
                color: '#FBBB3A',
              }}
            >
              Change Server storage period
            </span>
            <br />
            <span
              style={{
                marginTop: '10px',
                fontWeight: '500',
                whiteSpace: 'pre-line',
              }}
            >
              If you change the storage period, <br />
              some data may be deleted.
            </span>
          </>
        ),
        footer: (
          <>
            <Sub1Button
              onClick={(e) => {
                setDialogContents({ type: 'hidden' });
                postSettingData();
              }}
            >
              Yes, change it
            </Sub1Button>
            <PrimaryButton
              onClick={async () => {
                setDialogContents({ type: 'hidden' });
              }}
            >
              {`No, I don't change it`}
            </PrimaryButton>
          </>
        ),
      });
      return;
    }
  };

  /**
   * 실제 설정 데이터 저장
   */
  const postSettingData = async () => {
    if (!settingData) return;

    const url = `api/sw/setting`;
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    const _params: PostParams = {
      activeLanguages: [],
      diskCleanupRecoveryPercent: settingData.diskCleanupRecoveryPercent,
      diskFreeSpaceThresholdPercent: settingData.diskFreeSpaceThresholdPercent,
      dateFormat: settingData.dateFormat,
      encoding: settingData.encodings.filter((data) => data.active === true)[0].item,
      fileFormat: settingData.fileFormats.filter((data) => data.active === true)[0].format,
      isStorageWithoutTimeLimit: settingData.isStorageWithoutTimeLimit,
      language: settingData.defaultLanguage,
      storagePeriod: settingData.storagePeriod,
      sliceMin: Number(settingData.sliceMin) || null,
      sliceMax: Number(settingData.sliceMax) || null,
      timeout: Number(settingData.timeout),
      maximumTimeout: Number(settingData.maximumTimeout),
      timeZone: settingData.timeZone,
    };

    settingData.languages.forEach((data) => {
      if (data.active) _params.activeLanguages.push(data.language);
    });

    //Todo 데이터 적용후 확인 필요
    await getFetcher({
      method: 'POST',
      accessToken: token,
      body: _params,
    })(url)
      .then(async (r) => {
        if (r.code === '200') {
          successDialog();
          setOriginSettingData(settingData);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };
  /**
   * S/W 내부 메뉴 노출 리스트 호출
   */
  const getSettingMenuList = async () => {
    const url = 'api/menu/sw';
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          const _menuList: MenuListType[] = [];
          r.result.forEach((data: any) => {
            const _tmp = {
              enable: data.enable,
              name: data.name,
            };
            _menuList.push(_tmp);
          });

          setMenuList(_menuList);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * 메뉴 노출 여부
   * @param type
   */
  const isMenuVisible = (type: string) => {
    if (menuList.length === 0) return false;

    let _result = false;
    menuList.forEach((menu) => {
      if (menu.name === type && menu.enable) {
        _result = true;
      }
    });
    return _result;
  };

  /**
   * post 성공 레이어
   */
  const successDialog = () => {
    setDialogContents({
      type: 'success',
      body: (
        <>
          <div
            style={{
              margin: '10px auto',
              textAlign: 'center',
              color: '#000',
            }}
          >
            Success!
          </div>
        </>
      ),
      footer: (
        <>
          <PrimaryButton
            onClick={async () => {
              setDialogContents({ type: 'hidden' });
            }}
          >
            OK
          </PrimaryButton>
        </>
      ),
    });
  };

  /**
   * 언어 설정 element 반환
   */
  const makeLanguageSetting = () => {
    if (settingData && isMenuVisible('Language Setting')) {
      return (
        <>
          <SettingWrapper>
            <strong>Language Setting</strong>
            <SettingBox>
              <SettingList>
                <li>
                  <strong>Available language</strong>
                  <InputList>
                    {settingData.languages.map((list, index) => {
                      return (
                        <li key={index}>
                          <Input
                            type={'checkbox'}
                            name={list.language}
                            checked={list.active}
                            onChange={(e) => changeSetting(e, 'MULTILINGUAL_LANG', index)}
                          >
                            {list.language}
                          </Input>
                        </li>
                      );
                    })}
                  </InputList>
                </li>
                <li>
                  <span>Default language</span>
                  <SelectBox>
                    <Select
                      type={'DEFAULT_LANG'}
                      list={defaultLanguageList}
                      onSelected={changeSetting}
                      synchronize={true}
                    />
                  </SelectBox>
                </li>
              </SettingList>
            </SettingBox>
          </SettingWrapper>
        </>
      );
    }
  };

  /**
   * timezone element 반환
   */
  const makeTimeZoneSetting = () => {
    if (settingData && isMenuVisible('Time Zone') && timeZoneList.length > 0) {
      // 선택값 변환하여 반환
      const displayLabel = timeZoneList.filter((list) => {
        return list.value === settingData.timeZone;
      });
      return (
        <>
          <SettingWrapper>
            <strong>Time Zone</strong>
            <SettingBox>
              <SelectBox>
                <SelectSearch
                  selectList={timeZoneList}
                  label={displayLabel[0].label}
                  onChange={(e) => changeSetting(e, 'TIME_ZONE')}
                />
              </SelectBox>
            </SettingBox>
          </SettingWrapper>
        </>
      );
    }
  };

  /**
   * report save format element 반환
   */
  const makeReportSetting = () => {
    if (settingData && isMenuVisible('Report save format')) {
      return (
        <>
          <SettingWrapper>
            <strong>Report save format</strong>
            <SettingBox>
              <InputList>
                {settingData.fileFormats.map((list, index) => {
                  return (
                    <li key={index}>
                      <Input
                        type={`radio`}
                        name={list.format}
                        checked={list.active}
                        onChange={(e) => changeSetting(e, 'FILE_FORMAT', index)}
                      >
                        {list.format}
                      </Input>
                    </li>
                  );
                })}
              </InputList>
            </SettingBox>
          </SettingWrapper>
        </>
      );
    }
  };

  /**
   * disk 공간 세팅
   */
  const makeServerStorageSetting = () => {
    if (settingData && isMenuVisible('Server storage setting')) {
      return (
        <>
          <SettingWrapper>
            <strong>Server storage setting</strong>
            <SettingBox>
              <SettingList>
                <li>
                  <span>Auto delete</span>

                  <SelectBox style={{ width: 90, marginLeft: 30, marginRight: 30 }}>
                    <Select
                      synchronize
                      type={`STORE_AUTO_DELETE`}
                      list={AutoDelete}
                      selected={settingData.diskFreeSpaceThresholdPercent.toString()}
                      onSelected={confirmStorageSetting}
                    />
                  </SelectBox>

                  <span>Delete rate</span>

                  <SelectBox style={{ width: 90, marginLeft: 30, marginRight: 30 }}>
                    <Select
                      synchronize
                      type={`STORE_DELETE_RATE`}
                      selected={settingData.diskCleanupRecoveryPercent.toString()}
                      list={DeleteRate}
                      onSelected={confirmStorageSetting}
                    />
                  </SelectBox>
                </li>
                <li>
                  <div className="simple-deleted-caution">
                    <div>
                      If storage is less than <strong>{settingData.diskFreeSpaceThresholdPercent}%</strong>, it
                      automatically deletes <strong>{settingData.diskCleanupRecoveryPercent}%</strong> in the order of
                      old data.
                    </div>
                    <div>If another corporation exists in the server, the same value applies.</div>
                    <div>If not enough storage is available, the analysis may not proceed.</div>
                  </div>
                </li>
              </SettingList>
            </SettingBox>
          </SettingWrapper>
        </>
      );
    }
  };

  /**
   * servet storage period element 반환
   */
  const makeServerStoragePeriodSetting = () => {
    if (settingData && isMenuVisible('Server storage period')) {
      return (
        <>
          <SettingWrapper>
            <strong>Server storage period</strong>
            <SettingBox>
              <SettingList>
                <li>
                  <Input
                    type={`radio`}
                    name={`ServerStoragePeriod`}
                    value={'true'}
                    checked={settingData.isStorageWithoutTimeLimit}
                    onChange={(e) => confirmStoragePeriod(e, 'STORE_LIMIT')}
                  >
                    Keep it without a time limit.
                  </Input>
                </li>
                <li>
                  <Input
                    type={`radio`}
                    name={`ServerStoragePeriod`}
                    value={'false'}
                    checked={!settingData.isStorageWithoutTimeLimit}
                    onChange={(e) => confirmStoragePeriod(e, 'STORE_LIMIT')}
                  >
                    It will be automatically deleted
                  </Input>
                  <SelectBox className={'delete-period'}>
                    <Select
                      type={'STORE_LIMIT_DAY'}
                      list={limitDayList}
                      onSelected={confirmStoragePeriod}
                      disabled={settingData.isStorageWithoutTimeLimit}
                    />
                  </SelectBox>
                  <span> after the data upload. (Includes original and analysis results)</span>
                </li>
                <li>
                  <div className="deleted-caution">
                    It is deleted if the set period has passed regardless of the Server storage setting.
                  </div>
                </li>
              </SettingList>
            </SettingBox>
          </SettingWrapper>
        </>
      );
    }
  };

  /**
   * date Format element 반환
   */
  const makeDateFormatSetting = () => {
    if (settingData && isMenuVisible('Date format')) {
      return (
        <>
          <SettingWrapper>
            <strong>Date format</strong>
            <SettingBox>
              <InputList>
                {DateFormatList.map((list) => {
                  return (
                    <li key={list.value}>
                      <Input
                        className={'has-example'}
                        type={`radio`}
                        name={list.name}
                        value={list.value}
                        checked={settingData?.dateFormat === list.value}
                        onChange={(e) => changeSetting(e, 'DATE_FORMAT')}
                      >
                        {list.value}
                      </Input>
                      <span className={'example-format'}>
                        ex. {moment('2022-12-31', 'YYYY-MM-DD').format(list.value)}
                      </span>
                    </li>
                  );
                })}
              </InputList>
            </SettingBox>
          </SettingWrapper>
        </>
      );
    }
  };

  /**
   * encoding format element 반환
   */
  const makeEncodingFormat = () => {
    if (settingData && isMenuVisible('Encoding format')) {
      return (
        <>
          <SettingWrapper>
            <strong>Encoding format</strong>
            <SettingBox>
              <SelectBox>
                <Select
                  styles={css`
                    width: 300px;
                    font-size: 14px;
                  `}
                  type={'ENCODING_FORMAT'}
                  list={encofingList}
                  onSelected={changeSetting}
                />
              </SelectBox>
            </SettingBox>
          </SettingWrapper>
        </>
      );
    }
  };

  /**
   * identical series check 반환
   */
  const makeIdenticalSeriesCheck = () => {
    if (settingData && isMenuVisible('Identical series check')) {
      return (
        <>
          <SettingWrapper>
            <strong>Identical series check</strong>
            <SettingBox>
              <SettingList className={`identical`}>
                <li>
                  <span className={'label-identicalInput'}>Timeout</span>
                  <StyledSlice>
                    <span className={'label-satisfied'}>minimum slice satisfied</span>
                    <Input
                      className={'identicalInput'}
                      type={`text`}
                      name={'timeout'}
                      value={settingData.timeout || ''}
                      onChange={(e) => changeSetting(e, 'IDENTICAL_SERIES_CHECK_TIMEOUT')}
                      onBlur={(e) => timeoutBlur(e, 'IDENTICAL_SERIES_CHECK_TIMEOUT')}
                    />
                    <span className={'second'}>s</span>
                  </StyledSlice>
                  <StyledSlice className={`slice`}>
                    <span className={'label-satisfied'}>minimum slice not satisfied</span>
                    <Input
                      className={'identicalInput'}
                      type={`text`}
                      name={'timeout'}
                      value={settingData.maximumTimeout || ''}
                      onChange={(e) => changeSetting(e, 'IDENTICAL_SERIES_CHECK_MAXIMUM_TIMEOUT')}
                      onBlur={(e) => timeoutBlur(e, 'IDENTICAL_SERIES_CHECK_MAXIMUM_TIMEOUT')}
                    />
                    <span className={'second'}>s</span>
                  </StyledSlice>

                  <p className={'guide-text'}>※The timeout must be entered between 5 and 3600 seconds.</p>
                </li>
                <li>
                  <span className={'label-identicalInput'}>Slice Min</span>
                  <Input
                    className={'identicalInput'}
                    type={`text`}
                    name={'sliceMin'}
                    value={settingData.sliceMin || ''}
                    onChange={(e) => changeSetting(e, 'IDENTICAL_SERIES_CHECK_SLICE_MIN')}
                    onBlur={(e) => onBlurSliceTimeMinMax('min', e.target.value)}
                  />
                  <span className={'label-identicalInput'}>Slice Max</span>
                  <Input
                    className={'identicalInput last'}
                    type={`text`}
                    name={'sliceMax'}
                    value={settingData.sliceMax || ''}
                    onChange={(e) => changeSetting(e, 'IDENTICAL_SERIES_CHECK_SLICE_MAX')}
                    onBlur={(e) => onBlurSliceTimeMinMax('max', e.target.value)}
                  />
                  <p className={'guide-text'}>※MIN/MAX can be entered between 0 and 10000.</p>
                </li>
              </SettingList>
            </SettingBox>
          </SettingWrapper>
        </>
      );
    }
  };

  /**
   * submit 버튼 활성화 상태
   */
  useEffect(() => {
    const _sameData = _.isEqual(settingData, originSettingData);

    if ((settingData?.sliceMin && settingData?.sliceMax) || (!settingData?.sliceMin && !settingData?.sliceMax)) {
      //able
      setActiveSubmit(_sameData);
    } else {
      //disable
      setActiveSubmit(true);
    }
  }, [originSettingData, settingData]);

  /**
   * 최초 호출
   * - s/w 메뉴 노출 리스트
   * - s/w 셋팅값
   */
  useEffect(() => {
    (async () => {
      await getSettingMenuList();
      await getSettingData();
    })();
  }, []);

  /**
   * 세팅값에 따라 필요한 데이터 추가 호출
   */
  useEffect(() => {
    if (isMenuVisible('Time Zone')) {
      getTimeZone();
    }
  }, [menuList]);

  return (
    <>
      <label className={'sw-setting-title'}>Setting</label>
      {settingData && (
        <>
          {makeLanguageSetting()}
          {makeReportSetting()}
          {makeServerStorageSetting()}
          {makeServerStoragePeriodSetting()}
          {makeTimeZoneSetting()}
          {makeDateFormatSetting()}
          {makeEncodingFormat()}
          {makeIdenticalSeriesCheck()}
        </>
      )}
      <SwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            margin-top: 8px;
            font-size: 18px;
          `}
          onClick={submitDebounce(confirmSubmit)}
          disabled={activeSubmit}
        >
          Submit
        </PrimaryButton>
      </SwButtonSection>
    </>
  );
};
export default LanguageSettingPage;
